/*
    MAC Bespoke Web Timegrapher
    Copyright (C) 2026 MAC Bespoke Watch Co.

    This program is free software; you can redistribute it and/or modify
    it under the terms of the GNU General Public License version 2 as
    published by the Free Software Foundation.
*/

/*
   The approved v36 interface, taken across with its cascade intact.

   Several sections deliberately re-state earlier rules: the design was refined
   by appending overrides rather than editing in place, and the approved
   appearance depends on that order. Do not tidy it by merging blocks — the
   later rule is the one that was signed off. The React components reproduce the
   design's ids and class names for the same reason.

   Only two things are added, at the end: the layout glue React needs between
   <body> and the design's header/main/footer, and the custom properties the
   three graph canvases read for their own colours. The canvases are the one
   part of the interface the design did not cover, so they are adapted to it
   here rather than replaced.
*/
/* 1. Theme tokens and shared layout. Later sections preserve the approved responsive refinements. */

:root {
  color-scheme: light;
  --bg: #eeeae0;
  --ink: #292922;
  --muted: #78786d;
  --glass: rgba(255, 255, 250, 0.34);
  --edge: rgba(255, 255, 255, 0.78);
  --line: rgba(67, 64, 47, 0.1);
  --button: #31332b;
  --buttontext: #f6f3e9;
  --glow: rgba(255, 255, 249, 0.95);
  --shadow: rgba(79, 68, 39, 0.1);
}
:root[data-theme="dark"] {
  color-scheme: dark;
  --bg: #191a18;
  --ink: #eeede4;
  --muted: #a4a598;
  --glass: rgba(61, 64, 56, 0.25);
  --edge: rgba(241, 243, 217, 0.16);
  --line: rgba(236, 235, 216, 0.1);
  --button: #e8eadc;
  --buttontext: #26291f;
  --glow: rgba(151, 160, 111, 0.13);
  --shadow: rgba(0, 0, 0, 0.24);
}
* {
  box-sizing: border-box;
}
body {
  margin: 0;
  background: var(--bg);
  color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  min-height: 100svh;
  transition:
    background 0.4s,
    color 0.4s;
}
body:before {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 50% 37%, var(--glow), transparent 58%),
    radial-gradient(ellipse at 95% 92%, #91987318, transparent 45%);
}
button {
  font: inherit;
  cursor: pointer;
  color: inherit;
}
button:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 5px;
}
svg {
  display: block;
}
header {
  position: relative;
  max-width: 1440px;
  margin: auto;
  padding: 36px 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  gap: 11px;
  align-items: center;
  font-weight: 590;
  font-size: 17px;
  letter-spacing: -0.5px;
}
.brand svg {
  width: 25px;
  height: 25px;
}
.themes {
  padding: 4px;
  border: 1px solid var(--edge);
  border-radius: 30px;
  display: flex;
  background: var(--glass);
  box-shadow: 0 4px 15px var(--shadow);
  backdrop-filter: blur(20px);
}
.themes button {
  border: 0;
  background: transparent;
  width: 39px;
  height: 34px;
  border-radius: 22px;
  display: grid;
  place-items: center;
  color: var(--muted);
}
.themes button[aria-pressed="true"] {
  background: var(--button);
  color: var(--buttontext);
  box-shadow: 0 2px 5px var(--shadow);
}
.themes svg {
  width: 17px;
  height: 17px;
}
main {
  position: relative;
  min-height: calc(100svh - 168px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 25px 24px 60px;
}
.welcome {
  width: 100%;
  max-width: 460px;
  text-align: center;
}
.instrument {
  margin: 0 auto 37px;
  width: 184px;
  height: 184px;
  padding: 13px;
  border-radius: 50%;
  background: linear-gradient(
    140deg,
    var(--edge),
    transparent 45%,
    var(--glass)
  );
  border: 1px solid var(--edge);
  box-shadow:
    inset 0 0 30px var(--glass),
    0 22px 45px var(--shadow);
  backdrop-filter: blur(25px);
}
.instrument svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.dial {
  fill: none;
  stroke: var(--ink);
}
.tick {
  stroke: var(--muted);
  stroke-width: 1;
}
.trace {
  fill: none;
  stroke: var(--ink);
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 2.4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  margin: 0 0 15px;
}
h1 {
  font-size: clamp(37px, 6vw, 46px);
  line-height: 1.1;
  font-weight: 520;
  letter-spacing: -2px;
  margin: 0 0 18px;
}
.intro {
  font-size: 17px;
  line-height: 1.6;
  color: var(--muted);
  margin: 0 auto 33px;
  max-width: 325px;
}
.actions {
  padding: 9px;
  background: var(--glass);
  border: 1px solid var(--edge);
  border-radius: 29px;
  box-shadow:
    0 18px 45px var(--shadow),
    inset 0 1px 0 var(--edge);
  backdrop-filter: blur(30px);
}
.primary {
  height: 57px;
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 21px;
  background: var(--button);
  color: var(--buttontext);
  font-size: 16px;
  font-weight: 550;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  transition:
    transform 0.2s,
    opacity 0.2s;
}
.primary:hover {
  opacity: 0.87;
}
.primary:active {
  transform: scale(0.985);
}
.primary svg {
  width: 19px;
  height: 19px;
}
.secondary {
  height: 52px;
  border: 0;
  background: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
}
.secondary svg {
  width: 16px;
  height: 16px;
}
.note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: var(--muted);
  font-size: 12px;
  margin: 22px 0 0;
}
.note svg {
  height: 13px;
  width: 13px;
}
footer {
  position: relative;
  display: flex;
  justify-content: space-between;
  max-width: 1440px;
  margin: auto;
  padding: 0 52px 28px;
  font-size: 12px;
  color: var(--muted);
  letter-spacing: 0.2px;
}
footer button {
  border: 0;
  padding: 0;
  background: none;
  font-size: 12px;
  color: var(--muted);
}
dialog {
  color: var(--ink);
  background: var(--bg);
  border: 1px solid var(--edge);
  border-radius: 28px;
  padding: 30px;
  width: calc(100% - 40px);
  max-width: 420px;
  box-shadow: 0 30px 100px #0004;
}
dialog::backdrop {
  background: #10120e70;
  backdrop-filter: blur(12px);
}
dialog h2 {
  font-size: 26px;
  letter-spacing: -0.8px;
  font-weight: 550;
  margin: 0 0 18px;
}
dialog p {
  font-size: 16px;
  line-height: 1.6;
  color: var(--muted);
}
dialog .close {
  float: right;
  border: 0;
  background: var(--glass);
  border-radius: 50%;
  height: 32px;
  width: 32px;
  font-size: 22px;
  margin: -8px -8px 0 10px;
}
.steps {
  padding-left: 20px;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.6;
}
.steps li {
  padding: 7px 0;
}
.steps strong {
  color: var(--ink);
  font-weight: 550;
}
.values {
  display: flex;
  justify-content: space-between;
  margin: 24px 0;
}
.values b {
  display: block;
  font-size: 25px;
  font-weight: 500;
  margin-bottom: 7px;
}
.values span {
  font-size: 12px;
  color: var(--muted);
}
@media (max-width: 600px) {
  header {
    padding: 24px;
  }
  .brand {
    font-size: 16px;
  }
  main {
    min-height: calc(100svh - 148px);
    padding: 25px 25px 50px;
  }
  .welcome {
    max-width: 380px;
  }
  .instrument {
    width: 158px;
    height: 158px;
    margin-bottom: 32px;
  }
  footer {
    padding: 0 25px 24px;
  }
  .intro {
    font-size: 16px;
  }
  h1 {
    letter-spacing: -1.6px;
  }
}
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
  }
}

[hidden] {
  display: none !important;
}
header {
  position: sticky;
  top: 0;
  z-index: 10;
  max-width: none;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  height: 96px;
  padding: 20px 48px;
  background: var(--glass);
  border-bottom: 1px solid var(--edge);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
}
.logo {
  padding: 0;
  border: 0;
  background: none;
  width: 116px;
  justify-self: start;
  line-height: 0;
}
.logo img {
  width: 100%;
  mix-blend-mode: multiply;
}
html[data-theme="dark"] .logo img {
  filter: invert(1);
  mix-blend-mode: screen;
}
.app-title {
  font-size: 18px;
  font-weight: 560;
  letter-spacing: -0.5px;
}
.settings-button {
  justify-self: end;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--edge);
  background: var(--glass);
  border-radius: 50%;
  box-shadow: 0 4px 15px var(--shadow);
}
.settings-button svg {
  width: 22px;
  height: 22px;
}
.mode-actions {
  display: grid;
  gap: 12px;
}
.mode-card {
  display: flex;
  text-align: left;
  align-items: center;
  gap: 17px;
  width: 100%;
  padding: 22px;
  border: 1px solid var(--edge);
  border-radius: 24px;
  background: var(--glass);
  box-shadow:
    0 10px 25px var(--shadow),
    inset 0 1px 0 var(--edge);
  backdrop-filter: blur(24px);
  transition:
    transform 0.2s,
    background 0.2s;
}
.mode-card:hover {
  transform: translateY(-2px);
  background: var(--glow);
}
.mode-card strong {
  font-size: 18px;
  font-weight: 550;
  display: block;
  margin-bottom: 6px;
  letter-spacing: -0.3px;
}
.mode-card small {
  font-size: 14px;
  color: var(--muted);
}
.mode-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
}
.mode-icon svg {
  width: 23px;
  height: 23px;
}
.chevron {
  font-size: 26px;
  margin-left: auto;
  color: var(--muted);
}
.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 28px 0;
}
.workspace {
  max-width: 560px;
  width: 100%;
}
.workspace h1 {
  font-size: 36px;
}
.workspace .intro {
  margin: 20px 0;
  max-width: 100%;
}
.back {
  border: 0;
  background: none;
  padding: 0;
  margin: 0 0 32px;
  font-size: 14px;
  color: var(--muted);
}
.panel {
  padding: 25px;
  border: 1px solid var(--edge);
  border-radius: 24px;
  background: var(--glass);
  margin: 25px 0;
  box-shadow: 0 10px 35px var(--shadow);
}
label {
  display: block;
  font-size: 14px;
  margin-bottom: 20px;
}
input {
  display: block;
  margin-top: 10px;
  width: 100%;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--glass);
  color: var(--ink);
  font: inherit;
  font-size: 16px;
}
input:focus {
  outline: 2px solid var(--muted);
}
@media (max-width: 600px) {
  header {
    height: 84px;
    padding: 16px 22px;
  }
  .logo {
    width: 85px;
  }
  .app-title {
    font-size: 16px;
  }
  .settings-button {
    width: 40px;
    height: 40px;
  }
  .instrument {
    width: 140px;
    height: 140px;
    margin-bottom: 26px;
  }
  main {
    min-height: calc(100svh - 135px);
    padding-top: 32px;
  }
  .mode-card {
    padding: 18px;
    gap: 13px;
  }
  .mode-card small {
    font-size: 13px;
  }
  h1 {
    font-size: 36px;
  }
  .intro {
    margin-bottom: 25px;
  }
}
@media (max-width: 350px) {
  header {
    padding: 16px 14px;
  }
  .logo {
    width: 68px;
  }
  .app-title {
    font-size: 15px;
  }
  .mode-card {
    padding: 15px;
    gap: 10px;
  }
}

body {
  display: flex;
  flex-direction: column;
}
header {
  width: 100%;
  flex-shrink: 0;
}
main {
  flex: 1;
  min-height: 0;
  padding-top: 45px;
  padding-bottom: 48px;
}
.welcome-title {
  font-size: 25px;
  font-weight: 500;
  letter-spacing: 4px;
  margin: 0 0 32px;
}
.instrument {
  margin-bottom: 28px;
}
footer {
  width: 100%;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  text-align: center;
  padding: 0 24px 26px;
  line-height: 1.6;
  font-size: 12px;
}
footer p {
  margin: 0;
}
.source-label {
  text-decoration: underline;
  text-underline-offset: 3px;
}
@media (max-width: 600px) {
  main {
    min-height: 0;
    padding-top: 32px;
    padding-bottom: 38px;
  }
  .welcome-title {
    font-size: 23px;
    letter-spacing: 3px;
    margin-bottom: 28px;
  }
  footer {
    padding: 0 25px 24px;
    font-size: 12px;
  }
}
footer {
  gap: 6px;
}
.privacy-lock {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
}
.privacy-lock svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.app-title {
  letter-spacing: 4px;
}
@media (max-width: 600px) {
  .app-title {
    letter-spacing: 3px;
  }
}
@media (max-width: 350px) {
  header {
    grid-template-columns: 68px minmax(0, 1fr) 40px;
    gap: 8px;
  }
  .app-title {
    font-size: 12px;
    letter-spacing: 2px;
    text-align: center;
  }
}
#settings {
  max-width: 720px;
  padding: 30px;
  max-height: calc(100svh - 40px);
  overflow: auto;
}
#settings h2 {
  font-size: 24px;
  margin-bottom: 26px;
}
.settings-tabs {
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 4px;
  padding: 5px;
  border: 1px solid var(--edge);
  background: var(--glass);
  border-radius: 17px;
}
.settings-tabs button {
  border: 0;
  border-radius: 12px;
  padding: 12px 10px;
  font-size: 13px;
  white-space: nowrap;
  background: transparent;
  color: var(--muted);
}
.settings-tabs button[aria-selected="true"] {
  background: var(--button);
  color: var(--buttontext);
  box-shadow: 0 3px 10px var(--shadow);
}
.settings-content {
  padding-top: 30px;
  min-height: 390px;
}
.settings-content h3 {
  font-size: 26px;
  letter-spacing: -0.8px;
  font-weight: 500;
  margin: 0 0 13px;
}
.settings-content .section-intro {
  font-size: 15px;
  margin: 0 0 22px;
  line-height: 1.6;
}
.preferences-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 0;
  border-bottom: 1px solid var(--line);
}
.preferences-row strong,
.check-list strong {
  font-size: 15px;
  font-weight: 550;
}
.preferences-row p,
.check-list p {
  font-size: 14px;
  line-height: 1.5;
  margin: 7px 0 0;
}
.preferences-row .themes {
  flex-shrink: 0;
}
.settings-content .section-note {
  font-size: 12px;
  line-height: 1.5;
  margin: 22px 0 0;
  color: var(--muted);
}
.check-list {
  margin: 20px 0 25px;
}
.check-list > div {
  display: flex;
  gap: 17px;
  padding: 16px 0;
  border-bottom: 1px solid var(--line);
}
.check-list > div > span {
  font-size: 12px;
  color: var(--muted);
  padding-top: 3px;
  font-variant-numeric: tabular-nums;
}
.calibration-summary {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  background: var(--glass);
  border: 1px solid var(--edge);
  padding: 19px;
  border-radius: 16px;
  font-size: 14px;
}
.calibration-summary span {
  color: var(--muted);
}
.calibration-summary strong {
  font-weight: 500;
}
.settings-content details {
  border-bottom: 1px solid var(--line);
  padding: 18px 0;
}
.settings-content summary {
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
}
.settings-content details p {
  font-size: 14px;
  margin-bottom: 0;
}
.toggle {
  margin: 0;
  position: relative;
  display: block;
  width: 46px;
  height: 28px;
  flex-shrink: 0;
}
.toggle input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}
.toggle > span:first-of-type {
  display: block;
  width: 46px;
  height: 28px;
  border-radius: 20px;
  background: var(--line);
  border: 1px solid var(--edge);
  pointer-events: none;
}
.toggle > span:first-of-type:after {
  content: "";
  display: block;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: var(--muted);
  margin: 3px;
  transition: transform 0.2s;
}
.toggle input:checked + span {
  background: var(--button);
}
.toggle input:checked + span:after {
  background: var(--buttontext);
  transform: translateX(18px);
}
.toggle input:focus-visible + span {
  outline: 2px solid var(--ink);
  outline-offset: 4px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (max-width: 600px) {
  #settings {
    padding: 23px 20px;
    max-height: calc(100svh - 24px);
    width: calc(100% - 24px);
  }
  .settings-tabs {
    grid-template-columns: 1fr 1fr;
  }
  .settings-tabs button {
    padding: 11px 6px;
    font-size: 13px;
  }
  .settings-content {
    min-height: 360px;
    padding-top: 26px;
  }
  .settings-content h3 {
    font-size: 24px;
  }
  .preferences-row {
    gap: 12px;
  }
  .calibration-summary {
    font-size: 13px;
  }
}
#panel-general .preferences-row {
  padding: 17px 0;
  min-height: 66px;
}
#panel-general .preferences-row > label:not(.toggle) {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}
#panel-general select {
  font: inherit;
  font-size: 14px;
  color: var(--ink);
  background: var(--glass);
  border: 1px solid var(--edge);
  border-radius: 12px;
  padding: 11px 32px 11px 13px;
  max-width: 58%;
  min-width: 130px;
}
#panel-general select option {
  background: var(--bg);
  color: var(--ink);
}
#panel-general select:focus-visible,
.export-button:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 3px;
}
#panel-general .settings-section-heading {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: var(--muted);
  margin: 30px 0 0;
}
.export-button {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--edge);
  border-radius: 12px;
  background: var(--glass);
  padding: 11px 16px;
  font-size: 14px;
}
.export-button svg {
  width: 17px;
  height: 17px;
}
#panel-general .section-note:empty {
  display: none;
}
@media (max-width: 600px) {
  #panel-general .preferences-row {
    gap: 12px;
  }
  #panel-general select {
    font-size: 13px;
    min-width: 110px;
    max-width: 55%;
    padding-right: 20px;
  }
  #panel-general .preferences-row > label:not(.toggle) {
    font-size: 13px;
  }
}

#settings h2 {
  letter-spacing: 3px;
  font-size: 21px;
  font-weight: 500;
}
.settings-tabs button {
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-size: 11px;
}
#settings {
  max-width: 780px;
}
#panel-general .settings-group {
  margin-bottom: 26px;
}
#panel-general .settings-section-heading {
  margin: 0 0 12px;
  font-size: 12px;
  letter-spacing: 2.4px;
  font-weight: 500;
}
#panel-general .settings-group-surface {
  border: 1px solid var(--edge);
  border-radius: 20px;
  background: var(--glass);
  padding: 0 20px;
}
#panel-general .preferences-row {
  padding: 19px 0;
  gap: 22px;
}
#panel-general .preferences-row:last-child {
  border-bottom: 0;
}
#panel-general .option-copy {
  min-width: 0;
}
#panel-general .option-copy label {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 1.6px;
  margin: 0;
  line-height: 1.5;
}
#panel-general .option-copy p {
  font-size: 13px;
  line-height: 1.55;
  margin: 6px 0 0;
  color: var(--muted);
  max-width: 310px;
}
#panel-general select {
  flex-shrink: 0;
  max-width: 46%;
}
.export-button {
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-size: 12px;
  flex-shrink: 0;
}
#settings .close {
  font-size: 22px;
}
@media (max-width: 600px) {
  .settings-tabs button {
    font-size: 10px;
    letter-spacing: 1px;
  }
  #panel-general .settings-group-surface {
    padding: 0 15px;
  }
  #panel-general .preferences-row {
    gap: 15px;
  }
  #panel-general .option-copy label {
    font-size: 12px;
    letter-spacing: 1.2px;
  }
  #panel-general .option-copy p {
    font-size: 12px;
  }
  #panel-general select {
    min-width: 100px;
    max-width: 45%;
    font-size: 12px;
  }
  #panel-general .settings-section-heading {
    font-size: 11px;
    letter-spacing: 2px;
  }
  .export-button {
    font-size: 11px;
    padding: 11px 12px;
    gap: 8px;
  }
}
@media (max-width: 380px) {
  #panel-general .preferences-row:has(select) {
    align-items: stretch;
    flex-direction: column;
  }
  #panel-general select {
    max-width: 100%;
    width: 100%;
  }
}

/* Responsive and interaction review */
:root {
  --muted: #65665b;
}
html {
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
body {
  min-height: 100vh;
  min-height: 100svh;
}
header {
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 12px;
  height: auto;
  min-height: 84px;
  padding-left: max(24px, env(safe-area-inset-left));
  padding-right: max(24px, env(safe-area-inset-right));
  padding-top: max(16px, env(safe-area-inset-top));
}
.logo {
  max-width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
}
.settings-button {
  width: 44px;
  height: 44px;
}
.app-title {
  white-space: nowrap;
}
.mode-icon {
  flex-shrink: 0;
}
.mode-card > span:nth-child(2) {
  min-width: 0;
}
.mode-card small {
  font-size: 14px;
  line-height: 1.5;
  display: block;
}
.back {
  min-height: 44px;
  padding: 8px 0;
}
.values {
  gap: 12px;
  flex-wrap: wrap;
}
.values > div {
  min-width: 85px;
}
footer {
  padding-bottom: max(24px, env(safe-area-inset-bottom));
}
dialog {
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
  overflow: auto;
  overscroll-behavior: contain;
}
dialog .close,
#settings .close {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  margin: -8px -8px 0 8px;
}
#settings {
  padding-top: 0;
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
}
.settings-top {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bg);
  padding: 25px 0 14px;
}
#settings h2 {
  padding-top: 4px;
}
.settings-tabs {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.settings-tabs button {
  min-height: 44px;
  white-space: normal;
  overflow-wrap: normal;
  font-size: 12px;
  line-height: 1.45;
  padding: 10px 6px;
}
.settings-content {
  min-height: 0;
  padding-top: 16px;
}
.toggle {
  height: 44px;
  width: 48px;
  display: flex;
  align-items: center;
}
.toggle input {
  inset: 0;
  width: 48px;
  height: 44px;
}
.toggle > span:first-of-type {
  flex-shrink: 0;
}
#panel-general select {
  font-size: 16px;
  min-height: 44px;
}
.export-button {
  min-height: 44px;
}
.settings-content summary {
  min-height: 44px;
  padding: 10px 0;
}
.settings-content details {
  padding: 6px 0;
}
#panel-general .option-copy label {
  font-size: 14px;
}
#panel-general .option-copy p {
  font-size: 14px;
}
.settings-content .section-intro {
  font-size: 16px;
}
input,
select,
button {
  -webkit-tap-highlight-color: transparent;
}
@media (max-width: 600px) {
  header {
    gap: 10px;
  }
  .logo {
    width: 65px;
  }
  .app-title {
    font-size: 15px;
    letter-spacing: 3px;
  }
  .settings-tabs {
    grid-template-columns: 1fr 1fr;
  }
  #panel-general .preferences-row:has(select) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  #panel-general select {
    max-width: 100%;
    width: 100%;
    font-size: 16px;
  }
  #panel-general .option-copy label,
  #panel-general .option-copy p {
    font-size: 14px;
  }
  #settings .settings-tabs button {
    font-size: 12px;
  }
  .settings-top {
    padding-top: 20px;
  }
  .settings-content h3 {
    overflow-wrap: break-word;
  }
}
@media (max-width: 350px) {
  header {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    padding-left: 14px;
    padding-right: 14px;
    gap: 6px;
  }
  .logo {
    width: 45px;
  }
  .app-title {
    font-size: 13px;
    letter-spacing: 2px;
  }
  .mode-card {
    gap: 10px;
  }
  .mode-card small {
    font-size: 14px;
  }
}
@media (orientation: landscape) and (max-height: 500px) {
  header {
    min-height: 64px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  main {
    padding-top: 22px;
    padding-bottom: 28px;
  }
  .instrument {
    width: 100px;
    height: 100px;
    margin-bottom: 18px;
  }
  .welcome-title {
    margin-bottom: 20px;
  }
  #settings {
    max-height: calc(100dvh - 16px);
  }
  .settings-top {
    padding-top: 16px;
  }
  .settings-tabs {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  #settings h2 {
    margin-bottom: 14px;
  }
  .settings-content {
    padding-top: 12px;
  }
}

/* Compact phone layout */
@media (max-width: 600px) {
  footer {
    gap: 1px;
    line-height: 1.22;
    font-size: 12px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
  footer p {
    line-height: 1.22;
    margin: 0;
  }
  .privacy-lock {
    gap: 5px;
  }
  .privacy-lock svg {
    width: 12px;
    height: 12px;
  }
  main {
    padding-bottom: 26px;
  }
  #settings {
    padding-left: 16px;
    padding-right: 16px;
  }
  .settings-top {
    padding-top: 16px;
    padding-bottom: 8px;
  }
  #settings h2 {
    font-size: 19px;
    margin-bottom: 14px;
  }
  #settings .settings-tabs {
    display: flex;
    flex-wrap: nowrap;
    gap: 3px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: thin;
    border-radius: 14px;
    padding: 4px;
  }
  #settings .settings-tabs button {
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: 12px;
    letter-spacing: 0.6px;
    padding: 9px 10px;
    min-height: 44px;
    border-radius: 10px;
  }
  .settings-content {
    padding-top: 12px;
  }
  #panel-general .settings-group {
    margin-bottom: 17px;
  }
  #panel-general .settings-section-heading {
    margin-bottom: 7px;
    font-size: 11px;
    letter-spacing: 1.8px;
  }
  #panel-general .settings-group-surface {
    padding: 0 12px;
    border-radius: 17px;
  }
  #panel-general .preferences-row,
  #panel-general .preferences-row:has(select) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 11px 0;
    min-height: 60px;
  }
  #panel-general .option-copy {
    flex: 1;
  }
  #panel-general .option-copy label {
    font-size: 12px;
    letter-spacing: 0.8px;
    line-height: 1.25;
  }
  #panel-general .option-copy p {
    font-size: 12px;
    line-height: 1.25;
    margin-top: 3px;
    max-width: 100%;
  }
  #panel-general select {
    width: auto;
    max-width: 44%;
    min-width: 98px;
    font-size: 16px;
    min-height: 44px;
    padding: 8px 22px 8px 10px;
  }
  #panel-general .preferences-row:has(#movement) {
    flex-direction: column;
    align-items: stretch;
    gap: 7px;
  }
  #panel-general #movement {
    width: 100%;
    max-width: 100%;
  }
  .settings-content .section-intro {
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 14px;
  }
  .check-list {
    margin: 12px 0 18px;
  }
  .check-list > div {
    padding: 11px 0;
  }
  .check-list p,
  .preferences-row p {
    font-size: 12px;
    line-height: 1.3;
    margin-top: 3px;
  }
  .settings-content .section-note {
    margin-top: 12px;
    line-height: 1.3;
  }
  .settings-content details p {
    font-size: 13px;
    line-height: 1.4;
  }
}
@media (max-width: 350px) {
  #panel-general .preferences-row:has(select) {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
  #panel-general select {
    width: 100%;
    max-width: 100%;
  }
}

/* Consistent footer rhythm and adaptive settings tabs */
footer {
  gap: 1px;
  line-height: 1.22;
}
footer p {
  line-height: 1.22;
  margin: 0;
}
#settings .settings-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow-x: visible;
  gap: 4px;
}
#settings .settings-tabs button {
  white-space: normal;
  min-width: 0;
  line-height: 1.35;
  min-height: 44px;
  padding: 9px 7px;
}
@media (max-width: 600px) {
  #settings .settings-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 300px) {
  #settings .settings-tabs {
    grid-template-columns: minmax(0, 1fr);
  }
}
#settingsTitle:focus {
  outline: none;
}
.manual-movement {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 14px 0;
}
.manual-movement label {
  font-size: 12px;
  letter-spacing: 0.8px;
  margin: 0;
  line-height: 1.4;
}
.manual-movement label span {
  color: var(--muted);
  letter-spacing: 0;
}
.manual-movement input {
  margin-top: 5px;
  padding: 10px;
  min-height: 44px;
  font-size: 16px;
}
.manual-movement p {
  grid-column: 1/-1;
  margin: 0;
  font-size: 12px;
  line-height: 1.3;
  color: var(--muted);
}
.manual-movement p:empty {
  display: none;
}
.manual-movement input[aria-invalid="true"] {
  outline: 2px solid #b65943;
}
#movementSummary {
  font-variant-numeric: tabular-nums;
}
@media (max-width: 350px) {
  .manual-movement {
    grid-template-columns: 1fr;
  }
}

#panel-general select optgroup {
  background-color: #e3e0d6;
  color: #65665b;
  font-style: normal;
  font-weight: 600;
}
#panel-general select optgroup option {
  background-color: var(--bg);
  color: var(--ink);
  font-weight: 400;
}
html[data-theme="dark"] #panel-general select optgroup {
  background-color: #252722;
  color: #a4a598;
}
html[data-theme="dark"] #panel-general select optgroup option {
  background-color: #191a18;
  color: #eeede4;
}

.device-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.device-input-row > label {
  font-size: 12px;
  letter-spacing: 1.5px;
  margin: 0;
  font-weight: 500;
}
.device-input-row select {
  flex: 1;
  min-width: 0;
  font: inherit;
  font-size: 16px;
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid var(--edge);
  background: var(--glass);
  color: var(--ink);
  padding: 9px 26px 9px 12px;
}
.device-input-row option {
  background: var(--bg);
  color: var(--ink);
}
#settings .device-hint {
  font-size: 12px;
  line-height: 1.3;
  color: var(--muted);
  margin: 8px 0 0;
}
.device-progress {
  margin: 23px 0;
}
#deviceProgress {
  display: block;
  width: 100%;
  height: 5px;
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  background: var(--line);
  accent-color: var(--ink);
}
#deviceProgress::-webkit-progress-bar {
  background: var(--line);
  border-radius: 10px;
}
#deviceProgress::-webkit-progress-value {
  background: var(--muted);
  border-radius: 10px;
}
#deviceProgress::-moz-progress-bar {
  background: var(--muted);
  border-radius: 10px;
}
#settings #deviceProgressText {
  font-size: 12px;
  line-height: 1.35;
  color: var(--muted);
  margin: 8px 0 0;
  font-variant-numeric: tabular-nums;
}
#panel-device .settings-section-heading {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 2px;
  color: var(--muted);
  margin: 0 0 10px;
}
.device-check-rows {
  border: 1px solid var(--edge);
  border-radius: 18px;
  padding: 0 17px;
  background: var(--glass);
}
.device-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding: 12px 0;
  border-bottom: 1px solid var(--line);
}
.device-result:last-child {
  border-bottom: 0;
}
.device-result > div {
  min-width: 0;
}
.device-result strong {
  font-size: 14px;
  font-weight: 500;
}
#settings .device-result p {
  font-size: 12px;
  line-height: 1.35;
  margin: 3px 0 0;
  color: var(--muted);
  overflow-wrap: anywhere;
}
.check-badge {
  font-size: 11px;
  white-space: nowrap;
  border-radius: 7px;
  padding: 4px 7px;
  background: var(--line);
  color: var(--muted);
}
.check-badge[data-state="pass"] {
  color: #557053;
  background: #6d986514;
}
.check-badge[data-state="warn"] {
  color: #947238;
  background: #b89a5315;
}
.check-badge[data-state="fail"] {
  color: #b95b49;
  background: #b95b4915;
}
html[data-theme="dark"] .check-badge[data-state="pass"] {
  color: #b1c7a8;
}
html[data-theme="dark"] .check-badge[data-state="warn"] {
  color: #cfba8c;
}
html[data-theme="dark"] .check-badge[data-state="fail"] {
  color: #dfa496;
}
.device-summary {
  margin: 22px 0;
  padding-bottom: 17px;
  border-bottom: 1px solid var(--line);
}
#settings #deviceSummary {
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  color: var(--muted);
}
.device-export-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.device-export-row > span {
  font-size: 12px;
  letter-spacing: 1.5px;
}
.export-button:disabled {
  opacity: 0.4;
  cursor: default;
}
.device-input-row select:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 3px;
}
@media (max-width: 600px) {
  .device-input-row {
    gap: 8px;
  }
  .device-input-row > label {
    font-size: 11px;
    letter-spacing: 1px;
  }
  .device-input-row .export-button {
    font-size: 11px;
    letter-spacing: 0.5px;
    padding: 9px;
  }
  .device-input-row select {
    padding-left: 9px;
    font-size: 16px;
  }
  .device-progress {
    margin: 18px 0;
  }
  .device-check-rows {
    padding: 0 12px;
  }
  .device-result {
    padding: 10px 0;
  }
  .device-summary {
    margin: 17px 0;
  }
  .device-input-row {
    flex-wrap: wrap;
  }
  .device-input-row select {
    flex-basis: 45%;
  }
}
@media (max-width: 350px) {
  .device-input-row > label {
    width: 100%;
  }
}
.device-check-group {
  margin-bottom: 20px;
}
.movement-check-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  min-height: 44px;
  cursor: pointer;
}
.movement-check-heading input {
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  accent-color: var(--ink);
}
.movement-check-heading > span {
  font-size: 12px;
  letter-spacing: 2px;
  color: var(--muted);
  font-weight: 500;
}
.movement-check-heading small {
  display: block;
  font-size: 12px;
  line-height: 1.3;
  letter-spacing: 0;
  font-weight: 400;
  margin-top: 3px;
}
.check-badge[data-state="unavailable"] {
  color: var(--muted);
  background: var(--line);
}
@media (max-width: 600px) {
  .device-check-group {
    margin-bottom: 17px;
  }
}
#grantAudioPermission {
  flex: 1;
  justify-content: center;
}
#runDeviceCheck {
  flex-shrink: 0;
}
@media (max-width: 600px) {
  #grantAudioPermission {
    white-space: normal;
    line-height: 1.25;
  }
}
#inputHint:empty {
  display: none;
}
.device-export-row > div > span {
  font-size: 12px;
  letter-spacing: 1.5px;
}
#settings .device-export-row .device-hint {
  margin-top: 3px;
}

#quartzGrantPermission {
  flex: 1;
  justify-content: center;
}
#quartzInputHint:empty {
  display: none;
}
.quartz-preparation {
  margin: 23px 0;
}
#panel-quartz .settings-section-heading {
  font-size: 12px;
  letter-spacing: 2px;
  font-weight: 500;
  color: var(--muted);
  margin: 0 0 10px;
}
.quartz-prep-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
  cursor: pointer;
}
.quartz-prep-row:last-child {
  border-bottom: 0;
}
.quartz-prep-row input {
  height: 20px;
  width: 20px;
  flex-shrink: 0;
  margin: 2px 0 0;
  accent-color: var(--ink);
}
.quartz-prep-row strong {
  display: block;
  font-size: 14px;
  font-weight: 500;
}
.quartz-prep-row small {
  display: block;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 3px;
  color: var(--muted);
}
#quartzProgress {
  display: block;
  width: 100%;
  height: 5px;
  accent-color: var(--ink);
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  background: var(--line);
}
#quartzProgress::-webkit-progress-bar {
  background: var(--line);
}
#quartzProgress::-webkit-progress-value {
  background: var(--muted);
}
#quartzProgress::-moz-progress-bar {
  background: var(--muted);
}
#settings #quartzBeatCount {
  font-size: 12px;
  margin: 8px 0 0;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
#calibrationStart:disabled {
  opacity: 0.4;
  cursor: default;
}
.quartz-correction {
  margin-top: 24px;
  padding: 18px;
  border: 1px solid var(--edge);
  border-radius: 18px;
  background: var(--glass);
}
.quartz-correction > label {
  font-size: 12px;
  letter-spacing: 2px;
  color: var(--muted);
  margin: 0 0 12px;
}
.quartz-correction > div {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}
#quartzCorrection {
  width: 105px;
  min-height: 44px;
  font-size: 16px;
  padding: 10px;
  margin: 0;
  font-variant-numeric: tabular-nums;
}
#correctionSign {
  margin-right: -8px;
}
#settings .quartz-correction p {
  font-size: 12px;
  line-height: 1.35;
  margin: 8px 0 0;
  color: var(--muted);
}
#correctionError:empty {
  display: none;
}
#settings .quartz-reference-note {
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted);
  margin: 18px 0 0;
}
@media (max-width: 600px) {
  .quartz-preparation {
    margin: 18px 0;
  }
  .quartz-prep-row {
    padding: 11px 0;
  }
  .quartz-correction {
    margin-top: 18px;
    padding: 14px;
  }
  .quartz-correction > div {
    gap: 7px;
  }
  #quartzCorrection {
    width: 95px;
  }
}
.quartz-prep-row {
  cursor: default;
}
.prep-check {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--muted);
}

.instrument-workspace {
  width: 100%;
  max-width: 1120px;
}
.instrument-page main {
  align-items: flex-start;
  padding: 25px 40px 36px;
}
.instrument-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.instrument-heading .back {
  margin: 0;
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.instrument-heading .back span {
  font-size: 12px;
  letter-spacing: 2px;
}
.instrument-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}
.instrument-input {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 55%;
}
.microphone-symbol svg {
  width: 19px;
  height: 19px;
  color: var(--muted);
}
.plain-control,
.instrument-input select {
  background: var(--glass);
  border: 1px solid var(--edge);
  border-radius: 12px;
  min-height: 44px;
  padding: 10px 13px;
  font: inherit;
  font-size: 14px;
  color: var(--ink);
  min-width: 0;
  max-width: 100%;
}
.instrument-input option {
  color: var(--ink);
  background: var(--bg);
}
.movement-control {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 3px 12px;
  text-align: right;
  background: none;
  border: 0;
  padding: 4px;
  min-height: 44px;
}
.movement-control > span:first-child {
  font-size: 15px;
  font-weight: 550;
}
.movement-control small {
  grid-column: 1;
  font-size: 12px;
  color: var(--muted);
}
.movement-control > span:last-child {
  grid-column: 2;
  grid-row: 1/3;
}
.readout-surface,
.graph-surface,
.inspection-strip {
  background: var(--glass);
  border: 1px solid var(--edge);
  border-radius: 24px;
  box-shadow: 0 15px 40px var(--shadow);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}
.readout-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px 0;
}
.stability-status {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: var(--muted);
}
.reading-source {
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--muted);
}
.stability-mark {
  display: flex;
  align-items: center;
  gap: 3px;
  width: 23px;
  height: 20px;
}
.stability-mark i {
  width: 3px;
  height: 4px;
  border-radius: 5px;
  background: var(--muted);
}
.stability-mark[data-state="moving"] i {
  animation: settlingPulse 0.8s ease-in-out infinite alternate;
}
.stability-mark[data-state="settling"] i {
  animation: settlingPulse 1.6s ease-in-out infinite alternate;
}
.stability-mark i:nth-child(2) {
  animation-delay: 0.25s;
}
.stability-mark i:nth-child(3) {
  animation-delay: 0.5s;
}
.stability-mark[data-state="locked"] i {
  height: 10px;
  background: #77956b;
}
.measurement-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr 1fr 1.15fr;
  padding: 28px 25px 27px;
}
.measurement-stat {
  padding: 0 22px;
  border-left: 1px solid var(--line);
  min-width: 0;
}
.measurement-stat:first-child {
  border: 0;
  padding-left: 0;
}
.measurement-stat:last-child {
  padding-right: 0;
}
.stat-label {
  font-size: 11px;
  letter-spacing: 1.7px;
  color: var(--muted);
  display: block;
}
.measurement-stat > div {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin: 11px 0 8px;
  white-space: nowrap;
}
.measurement-stat strong {
  font-size: clamp(34px, 4.2vw, 51px);
  font-weight: 450;
  letter-spacing: -2px;
  font-variant-numeric: tabular-nums;
}
.measurement-stat:last-child strong {
  font-size: clamp(30px, 3.7vw, 44px);
}
.stat-unit {
  font-size: 12px;
  color: var(--muted);
}
.measurement-stat small {
  font-size: 12px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.signal-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 25px;
  border-top: 1px solid var(--line);
}
.signal-label {
  font-size: 10px;
  letter-spacing: 1.5px;
  color: var(--muted);
}
.db-meter {
  height: 7px;
  flex: 1;
  max-width: 330px;
  background: var(--line);
  border-radius: 6px;
  overflow: hidden;
}
.db-meter > div {
  height: 100%;
  width: 0;
  background: #8a9675;
  border-radius: 6px;
  transition: width 0.4s;
}
.signal-db {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
}
.signal-quality {
  font-size: 12px;
  margin-left: auto;
}
.graph-surface {
  margin-top: 18px;
  overflow: hidden;
}
.graph-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 17px;
  border-bottom: 1px solid var(--line);
}
.graph-tabs {
  display: flex;
  gap: 4px;
}
.graph-tabs button {
  font-size: 13px;
  border: 0;
  border-radius: 11px;
  padding: 10px 17px;
  min-height: 44px;
  background: none;
  color: var(--muted);
}
.graph-tabs button[aria-selected="true"] {
  background: var(--button);
  color: var(--buttontext);
}
#graphScale {
  font-size: 12px;
  color: var(--muted);
}
#measurementGraph {
  position: relative;
  height: 250px;
  margin: 0 18px;
}
#instrumentGraph {
  width: 100%;
  height: 100%;
  display: block;
}
#graphEmpty {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 13px;
  color: var(--muted);
}
.graph-axis {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid var(--line);
  padding: 10px 18px;
  font-size: 10px;
  color: var(--muted);
  letter-spacing: 0.8px;
}
.instrument-notice {
  font-size: 12px;
  color: var(--muted);
  text-align: center;
  line-height: 1.4;
  margin: 16px 0 0;
}
.inspection-strip {
  padding: 17px 22px;
  margin-top: 18px;
}
.position-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.position-top strong {
  display: block;
  font-size: 18px;
  font-weight: 500;
  margin-top: 6px;
}
.position-track {
  display: flex;
  gap: 8px;
  margin-top: 15px;
}
.position-track button {
  flex: 1;
  border: 1px solid var(--line);
  background: none;
  border-radius: 10px;
  min-height: 44px;
  font-size: 12px;
  color: var(--muted);
}
.position-track button[aria-pressed="true"] {
  border-color: var(--muted);
  color: var(--ink);
  background: var(--glass);
}
.position-track button[data-captured="true"] {
  background: var(--line);
}
#inspectionCaptureNote {
  font-size: 12px;
  color: var(--muted);
  margin: 10px 0 0;
}
.instrument-page footer {
  font-size: 11px;
}
@keyframes settlingPulse {
  to {
    height: 16px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .stability-mark i {
    animation: none !important;
  }
  .stability-mark[data-state="moving"] i {
    height: 15px;
  }
  .stability-mark[data-state="settling"] i {
    height: 8px;
  }
}
@media (max-width: 800px) {
  .measurement-grid {
    grid-template-columns: 1fr 1fr;
    padding: 22px;
  }
  .measurement-stat {
    padding: 0 0 20px 22px;
  }
  .measurement-stat:nth-child(3) {
    border-left: 0;
    padding: 20px 22px 0 0;
    border-top: 1px solid var(--line);
  }
  .measurement-stat:nth-child(4) {
    padding: 20px 0 0 22px;
    border-top: 1px solid var(--line);
  }
  .measurement-stat strong,
  .measurement-stat:last-child strong {
    font-size: 46px;
  }
  .instrument-page main {
    padding: 18px 24px 28px;
  }
  .stat-label {
    font-size: 11px;
  }
}
@media (max-width: 480px) {
  .instrument-page main {
    padding: 12px 16px 24px;
  }
  .instrument-heading {
    margin-bottom: 9px;
  }
  .instrument-toolbar {
    gap: 10px;
    margin-bottom: 14px;
  }
  .instrument-input {
    max-width: 53%;
    gap: 5px;
  }
  .instrument-input select {
    font-size: 16px;
    width: 100%;
    padding-left: 8px;
  }
  .plain-control {
    font-size: 12px;
    padding: 9px;
  }
  .microphone-symbol {
    display: none;
  }
  .movement-control > span:first-child {
    font-size: 14px;
  }
  .movement-control small {
    font-size: 11px;
  }
  .readout-top {
    padding: 15px 17px 0;
  }
  .reading-source {
    font-size: 10px;
    letter-spacing: 0.7px;
  }
  .measurement-grid {
    padding: 21px 17px;
  }
  .measurement-stat strong,
  .measurement-stat:last-child strong {
    font-size: 40px;
  }
  .measurement-stat {
    padding-left: 16px;
  }
  .measurement-stat:nth-child(4) {
    padding-left: 16px;
  }
  .measurement-stat > div {
    gap: 5px;
  }
  .stat-unit {
    font-size: 11px;
  }
  .signal-strip {
    padding: 12px 17px;
    gap: 9px;
  }
  .signal-label {
    display: none;
  }
  .signal-db,
  .signal-quality {
    font-size: 11px;
  }
  .graph-toolbar {
    padding: 8px 10px;
    gap: 5px;
  }
  .graph-tabs {
    gap: 1px;
  }
  .graph-tabs button {
    padding: 9px 10px;
    font-size: 12px;
  }
  #graphScale {
    font-size: 10px;
  }
  #measurementGraph {
    height: 205px;
    margin: 0 12px;
  }
  .inspection-strip {
    padding: 15px 17px;
  }
  .position-track {
    gap: 5px;
  }
}
@media (orientation: landscape) and (max-height: 500px) and (min-width: 650px) {
  .measurement-grid {
    grid-template-columns: repeat(4, 1fr);
    padding: 20px;
  }
  .measurement-stat,
  .measurement-stat:nth-child(3),
  .measurement-stat:nth-child(4) {
    border-top: 0;
    padding: 0 12px;
    border-left: 1px solid var(--line);
  }
  .measurement-stat:first-child {
    border: 0;
    padding-left: 0;
  }
  .measurement-stat strong,
  .measurement-stat:last-child strong {
    font-size: 32px;
  }
  #measurementGraph {
    height: 170px;
  }
}

header {
  transition:
    min-height 0.35s ease,
    padding 0.35s ease,
    background 0.35s ease;
}
.logo {
  transition: width 0.35s ease;
}
.app-title {
  transition:
    font-size 0.35s ease,
    letter-spacing 0.35s ease;
}
.instrument-page header {
  min-height: 64px;
  padding-top: max(8px, env(safe-area-inset-top));
  padding-bottom: 8px;
}
.instrument-page header .logo {
  width: 86px;
}
.instrument-page header .app-title {
  font-size: 15px;
  letter-spacing: 3px;
}
.instrument-page main {
  padding-top: 8px;
}
.instrument-heading {
  margin-bottom: 5px;
}
.instrument-toolbar {
  margin-bottom: 14px;
}
.instrument-input {
  max-width: 65%;
  flex: 1;
}
.reading-toggle {
  flex-shrink: 0;
  min-height: 44px;
  padding: 9px 18px;
  border: 1px solid var(--edge);
  border-radius: 12px;
  background: var(--button);
  color: var(--buttontext);
  font: inherit;
  font-size: 13px;
}
.readout-top {
  justify-content: center;
  padding-top: 18px;
}
.stability-status {
  flex-direction: column;
  gap: 5px;
  align-items: center;
  text-align: center;
  width: 100%;
  font-size: 16px;
  letter-spacing: 2px;
  font-weight: 550;
  text-transform: uppercase;
}
.stability-mark {
  width: 86px;
  height: 20px;
  justify-content: center;
  gap: 6px;
}
.stability-mark i {
  width: 20px;
  height: 4px;
  transition:
    height 0.35s ease,
    background 0.35s ease;
}
.stability-mark[data-state="locked"] i {
  height: 5px;
  background: #77956b;
}
.stability-mark[data-state="paused"] i {
  height: 4px;
  animation: none;
}
.measurement-grid {
  padding-top: 20px;
}
#settings[open] {
  animation: settingsReveal 0.3s cubic-bezier(0.2, 0.7, 0.2, 1);
}
#settings[open]::backdrop {
  animation: backdropReveal 0.3s ease;
}
@keyframes settingsReveal {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.975);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes backdropReveal {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@media (max-width: 480px) {
  .instrument-page main {
    padding-top: 4px;
  }
  .instrument-page header .logo {
    width: 54px;
  }
  .instrument-page header .app-title {
    font-size: 13px;
    letter-spacing: 2.5px;
  }
  .instrument-heading {
    margin-bottom: 3px;
  }
  .instrument-toolbar {
    flex-wrap: wrap;
    gap: 8px;
  }
  .instrument-input {
    max-width: 100%;
    flex-basis: 100%;
  }
  .instrument-input select {
    flex: 1;
    width: auto;
  }
  .instrument-input .plain-control {
    flex: 1;
  }
  .reading-toggle {
    padding: 9px 16px;
  }
  .movement-control {
    margin-left: auto;
  }
  .stability-status {
    font-size: 15px;
  }
  .readout-top {
    padding-top: 15px;
  }
  .measurement-grid {
    padding-top: 17px;
  }
}
@media (prefers-reduced-motion: reduce) {
  header,
  .logo,
  .app-title,
  .stability-mark i {
    transition: none !important;
  }
  #settings[open],
  #settings[open]::backdrop {
    animation: none !important;
  }
}

.instrument-input .plain-control {
  flex: 1;
  width: 100%;
}
.instrument-input:has(#instrumentGrant:not([hidden])) .microphone-symbol {
  display: none;
}
.stability-status {
  flex-direction: row;
  justify-content: center;
  gap: 12px;
}
.stability-mark {
  flex-shrink: 0;
}
.readout-top {
  padding-top: 14px;
}
.measurement-grid {
  padding-top: 15px;
}
@media (max-width: 480px) {
  .stability-status {
    flex-direction: row;
    gap: 9px;
    font-size: 14px;
    letter-spacing: 1.4px;
  }
  .stability-mark {
    width: 70px;
    gap: 5px;
  }
  .stability-mark i {
    width: 17px;
  }
  .readout-top {
    padding-top: 12px;
  }
  .measurement-grid {
    padding-top: 14px;
  }
}

/* Compact stability range and independent reading reset. */
.readout-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px !important;
}
.readout-top .stability-status {
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
}
.stability-label {
  font-size: 11px;
  letter-spacing: 1.4px;
  font-weight: 600;
  color: var(--muted);
  opacity: 0.55;
  transition:
    color 0.3s,
    opacity 0.3s;
}
#stabilityMoving.active {
  color: var(--ink);
  opacity: 1;
}
#stabilityLocked.active {
  color: #77956b;
  opacity: 1;
}
.readout-top .stability-mark {
  position: relative;
  display: block;
  flex: 1;
  width: auto;
  max-width: 300px;
  min-width: 36px;
  height: 10px;
  border-radius: 20px;
  background: var(--line);
  overflow: hidden;
}
.locked-range {
  position: absolute;
  inset: 0 0 0 76%;
  background: rgba(119, 149, 107, 0.24);
  border-left: 1px solid rgba(119, 149, 107, 0.4);
}
#stabilityCursor {
  position: absolute;
  top: 1px;
  bottom: 1px;
  width: 18px;
  transform: translateX(-50%);
  border-radius: 8px;
  background: var(--ink);
  transition:
    left 0.65s linear,
    background 0.3s;
  box-shadow: 0 0 8px rgba(119, 149, 107, 0.2);
}
#stabilityMark[data-state="locked"] #stabilityCursor {
  background: #77956b;
}
#stabilityMark[data-state="idle"] #stabilityCursor {
  opacity: 0;
}
#stabilityMark[data-state="paused"] #stabilityCursor {
  opacity: 0.4;
}
.reset-readings {
  display: grid;
  place-items: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}
.reset-readings:hover {
  background: var(--line);
  color: var(--ink);
}
.reset-readings:focus-visible {
  outline: 2px solid var(--muted);
  outline-offset: 1px;
}
.stability-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
@media (max-width: 480px) {
  .readout-top .stability-status {
    gap: 8px;
  }
  .stability-label {
    font-size: 10px;
    letter-spacing: 1px;
  }
  .readout-top {
    padding: 3px 8px !important;
  }
}
@media (prefers-reduced-motion: reduce) {
  #stabilityCursor {
    transition: none;
  }
}

/* Keep input, transport and movement on one compact phone row. */
@media (max-width: 600px) {
  .instrument-toolbar {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    align-items: center;
  }
  .instrument-toolbar .instrument-input {
    flex: 1 1 0;
    flex-basis: 0;
    min-width: 0;
    max-width: none;
    gap: 5px;
  }
  .instrument-input .microphone-symbol {
    display: none;
  }
  .instrument-input select {
    flex: 1 1 0;
    width: 0;
    min-width: 0;
    text-overflow: ellipsis;
    padding: 9px 5px;
    font-size: 16px;
  }
  .instrument-input .plain-control {
    min-width: 0;
    padding: 9px 6px;
    font-size: 13px;
    white-space: nowrap;
  }
  .instrument-input .reading-toggle {
    padding: 9px 10px;
    font-size: 12px;
    min-height: 44px;
  }
  .instrument-toolbar .movement-control {
    flex: 0 0 112px;
    min-width: 0;
    max-width: 112px;
    margin-left: 0;
    padding: 3px 0;
    gap: 2px 4px;
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .movement-control > span:first-child {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .movement-control small {
    font-size: 10px;
    letter-spacing: 0;
    white-space: nowrap;
    line-height: 1.25;
  }
}

.inspection-strip {
  margin: 0 0 12px;
  padding: 14px 16px;
}
.wizard-input .instrument-toolbar {
  margin-bottom: 12px;
}
.capture-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.capture-actions label {
  font-size: 12px;
  color: var(--muted);
  white-space: nowrap;
}
.position-top strong {
  display: block;
  margin-top: 4px;
}
.inspection-summary {
  width: min(570px, calc(100% - 24px));
  max-height: 90dvh;
  overflow: auto;
  padding: 28px;
}
.inspection-summary h2 {
  font-size: 18px;
  letter-spacing: 2px;
  margin: 8px 30px 20px 0;
}
.inspection-summary > label {
  display: block;
  font-size: 11px;
  letter-spacing: 1px;
  margin: 15px 0;
}
.inspection-summary input:not([type="radio"]),
.inspection-summary textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin-top: 6px;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--glass);
  color: var(--ink);
  font: inherit;
  font-size: 16px;
}
.inspection-summary fieldset {
  border: 0;
  padding: 0;
  margin: 18px 0;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.inspection-summary legend {
  font-size: 11px;
  letter-spacing: 1px;
  margin-bottom: 8px;
}
.inspection-summary fieldset label {
  font-size: 12px;
}
.summary-table-wrap table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.summary-table-wrap td,
.summary-table-wrap th {
  text-align: left;
  padding: 9px 4px;
  border-bottom: 1px solid var(--line);
}
.summary-table-wrap th {
  font-size: 10px;
  color: var(--muted);
}
.summary-units {
  font-size: 11px;
  color: var(--muted);
  line-height: 1.4;
}
.instrument-heading {
  gap: 8px;
}
#inspectionSummaryButton {
  margin-left: auto;
  font-size: 12px;
  min-height: 36px;
  padding: 6px 10px;
}
@media (max-width: 480px) {
  .inspection-strip {
    padding: 10px;
  }
  .capture-actions {
    gap: 6px;
  }
  .capture-actions label {
    font-size: 11px;
  }
  .position-top strong {
    font-size: 17px;
  }
  .instrument-heading .back {
    font-size: 12px;
  }
}

/* Wizard progress is informational, not position navigation. */
.capture-actions label {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  margin: 0;
  min-height: 36px;
}
.capture-actions #autoCapture {
  appearance: auto;
  display: inline-block;
  flex: 0 0 14px;
  width: 14px;
  height: 14px;
  min-height: 0;
  padding: 0;
  margin: 0;
  vertical-align: middle;
  accent-color: var(--ink);
}
.wizard-feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}
.wizard-feedback #inspectionCaptureNote {
  margin: 0;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
}
.wizard-feedback .position-track {
  display: flex;
  flex-shrink: 0;
  gap: 5px;
  margin: 0;
}
.position-indicator {
  display: grid;
  place-items: center;
  width: 22px;
  height: 26px;
  color: var(--muted);
  opacity: 0.3;
}
.position-indicator[data-current="true"] {
  color: var(--ink);
  opacity: 1;
}
.position-indicator[data-captured="true"] {
  color: #77956b;
  opacity: 1;
}
.position-check {
  display: none;
}
.position-indicator[data-captured="true"] .position-check {
  display: block;
}
.position-indicator[data-captured="true"] .position-hand {
  display: none;
}
@media (max-width: 480px) {
  .wizard-feedback {
    gap: 8px;
  }
  .wizard-feedback .position-track {
    gap: 2px;
  }
  .position-indicator {
    width: 18px;
  }
  .wizard-feedback #inspectionCaptureNote {
    font-size: 11px;
  }
}

/* Broader transport control and clearer cream-mode controls. */
.instrument-input .reading-toggle {
  flex: 1 1 90px;
  min-width: 68px;
}
.instrument-input:has(#instrumentInput:not([hidden])) select {
  flex: 1 1 140px;
  width: 0;
}
:root:not([data-theme="dark"])
  :is(
    .plain-control,
    .reading-toggle,
    .export-button,
    select,
    .primary,
    .themes,
    .settings-tabs button
  ) {
  border: 1px solid rgba(67, 64, 47, 0.36);
}
.inspection-summary .summary-sticky {
  position: sticky;
  top: -28px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--bg);
  margin: -28px -28px 14px;
  padding: 18px 20px 10px 28px;
}
.inspection-summary .summary-sticky h2 {
  margin: 0;
  font-size: 16px;
}
.inspection-summary .summary-sticky .close {
  position: static;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  margin: 0;
}
.inspection-summary .inline-radio {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 16px;
}
.inspection-summary .inline-radio label {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  margin: 0;
}
.inspection-summary .inline-radio input {
  appearance: auto;
  flex: 0 0 14px;
  width: 14px;
  height: 14px;
  min-height: 0;
  margin: 0;
  padding: 0;
  accent-color: var(--ink);
}
.inspection-summary .inline-radio > span {
  font-size: 12px;
  color: var(--muted);
}
@media (max-width: 600px) {
  .instrument-input .reading-toggle {
    flex: 1 1 68px;
  }
  .instrument-input:has(#instrumentInput:not([hidden])) select {
    flex: 1 1 85px;
  }
}
.inspection-summary .pdf-sizes {
  flex-wrap: wrap;
  gap: 10px 16px;
}
.inspection-summary .pdf-sizes > span {
  display: none;
}
#inspectionSummaryButton {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  min-height: 40px;
  padding: 0;
  flex: 0 0 40px;
}
#inspectionSummaryButton[hidden] {
  display: none;
}

/* ------------------------------------------------------------------------ */
/* Added for the React port. Nothing above this line is ours.                */
/* ------------------------------------------------------------------------ */

/*
   React mounts into #root, which the design's markup does not have. `contents`
   removes the wrapper from layout entirely so header, main and footer remain
   flex children of <body>, which is what every rule above assumes.
*/
#root {
  display: contents;
}

/*
   Colours the three graph canvases read out of the cascade at draw time, so
   the strip follows the theme the same way the rest of the interface does.
   They are resolved per draw rather than cached, because the operator can
   change the system appearance while a measurement is running.

   The trace is ink on paper in both themes — that is what a paper-tape
   timegrapher was — so it takes the foreground colour rather than an accent.
*/
:root {
  --text-dim: var(--muted);
  --text-faint: rgba(101, 102, 91, 0.62);
  --ok: #77956b;

  --trace-ink: #292922;
  --trace-ink-faded: rgba(41, 41, 34, 0.26);
  --trace-rule: rgba(67, 64, 47, 0.14);
  --trace-rule-centre: rgba(67, 64, 47, 0.32);
  --wave-line: #292922;
  --wave-axis: rgba(67, 64, 47, 0.18);
}

:root[data-theme="dark"] {
  --text-faint: rgba(164, 165, 152, 0.6);
  --ok: #8fae82;

  --trace-ink: #eeede4;
  --trace-ink-faded: rgba(238, 237, 228, 0.28);
  --trace-rule: rgba(236, 235, 216, 0.12);
  --trace-rule-centre: rgba(236, 235, 216, 0.26);
  --wave-line: #eeede4;
  --wave-axis: rgba(236, 235, 216, 0.15);
}

/*
   The canvases fill the graph well the design sized for an SVG. They measure
   their own box and size their backing store from it, so all this has to do is
   give them one.
*/
#measurementGraph > canvas {
  display: block;
  width: 100%;
  height: 100%;
}

/* The engine's own failures, in the space the design left for a notice. */
.instrument-notice[data-tone="bad"] {
  color: #b95b49;
}
html[data-theme="dark"] .instrument-notice[data-tone="bad"] {
  color: #dfa496;
}

/* A select with nothing to choose from yet still has to hold its place in the
   toolbar rather than collapsing to the width of its arrow. */
.instrument-input select:disabled,
.device-input-row select:disabled {
  opacity: 0.55;
}

/*
   The settled range, drawn as an oval rather than as a shaded rectangle with a
   rule down its left edge.

   It is a destination, not a boundary: the cursor arriving inside it is the
   locked verdict itself, computed from the same four criteria, so the shape
   should read as somewhere to get to. Its left edge is set inline from the same
   constant that scales the cursor — see stability-position.ts — so the two
   cannot drift apart.
*/
.readout-top .locked-range {
  right: 0;
  border-left: 0;
  border-radius: 20px;
  background: rgba(119, 149, 107, 0.22);
  box-shadow: inset 0 0 0 1px rgba(119, 149, 107, 0.5);
  transition: background 0.3s;
}
#stabilityMark[data-state="locked"] .locked-range {
  background: rgba(119, 149, 107, 0.42);
  box-shadow: inset 0 0 0 1px rgba(119, 149, 107, 0.75);
}
html[data-theme="dark"] .readout-top .locked-range {
  background: rgba(143, 174, 130, 0.18);
  box-shadow: inset 0 0 0 1px rgba(143, 174, 130, 0.42);
}
html[data-theme="dark"] #stabilityMark[data-state="locked"] .locked-range {
  background: rgba(143, 174, 130, 0.38);
  box-shadow: inset 0 0 0 1px rgba(143, 174, 130, 0.7);
}
html[data-theme="dark"] #stabilityMark[data-state="locked"] #stabilityCursor {
  background: #8fae82;
}

/*
   The row the check is working on.

   A list of fifteen that fills in silently is a progress bar with extra steps.
   Marked rather than animated: the check moves down the list at its own pace —
   ten seconds on audio timing, a tenth on the ones that are a property read —
   and a spinner would imply all fifteen take the same effort.
*/
.device-result[data-active] {
  background: var(--glow);
  box-shadow: inset 2px 0 0 var(--muted);
  border-radius: 8px;
  margin: 0 -8px;
  padding-left: 10px;
  padding-right: 8px;
}
html[data-theme="dark"] .device-result[data-active] {
  background: rgba(236, 235, 216, 0.05);
}
.device-result[data-active] strong {
  color: var(--ink);
}
.check-badge[data-state="running"] {
  color: var(--ink);
  background: var(--line);
}
.check-badge[data-state="skipped"] {
  color: var(--muted);
  background: transparent;
  opacity: 0.6;
}
/* A skipped row is not a result. It should read as absent, not as an outcome. */
.device-result:has(.check-badge[data-state="skipped"]) {
  opacity: 0.55;
}

/* The check takes half a minute with the movement steps; the bar has to be
   readable while it crawls rather than only at the ends. */
#deviceProgress {
  height: 6px;
}
#settings #deviceProgressText {
  min-height: 2.4em;
}

/*
   A reason to distrust a reading, under the reading it applies to.

   Amber rather than red: the figure is real and worth having, it just cannot be
   trusted on this browser. Sized and wrapped to the column it sits in, so it
   costs the readout two short lines rather than widening the grid — and only on
   the platform where the fault was measured, which is Chrome for Android on the
   communication route. See audio/capture-route.ts.
*/
.measurement-stat .stat-caveat {
  display: block;
  margin-top: 3px;
  max-width: 15em;
  font-size: 11px;
  line-height: 1.25;
  letter-spacing: 0;
  font-variant-numeric: normal;
  text-wrap: pretty;
  color: #947238;
}
html[data-theme="dark"] .measurement-stat .stat-caveat {
  color: #cfba8c;
}
@media (max-width: 480px) {
  .measurement-stat .stat-caveat {
    font-size: 10.5px;
  }
}

/* Export sits beside the way to start the next watch, because finishing one
   inspection and beginning another is the same moment. */
.inspection-summary .summary-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

/*
   The transport reads as three different actions, not one button with three
   labels.

   Start is filled, because it is the one thing to do. Pause is not: while a
   reading runs the button is a way to interrupt it, and dressing it the same
   meant an operator glancing down mid-inspection could not tell from the colour
   whether it was time to move the watch. Clear is quieter still — it appears
   only when the run is over and it throws the readings away.
*/
.instrument-input .reading-toggle[data-action="pause"],
.instrument-input .reading-toggle[data-action="clear"] {
  background: var(--glass);
  color: var(--ink);
  border: 1px solid var(--edge);
}
:root:not([data-theme="dark"])
  .instrument-input .reading-toggle[data-action="pause"],
:root:not([data-theme="dark"])
  .instrument-input .reading-toggle[data-action="clear"] {
  border-color: rgba(67, 64, 47, 0.36);
}
.instrument-input .reading-toggle[data-action="clear"] {
  color: var(--muted);
}

/*
   The summary heading takes focus when the dialog opens, so a screen reader
   announces what just appeared. It should not draw a focus ring for it: nobody
   tabbed there, and the blue box read as a selected text field.
*/
#summaryTitle:focus,
#settingsTitle:focus {
  outline: none;
}

/* The run's average, under the readings it comes from. */
.summary-table-wrap tfoot th,
.summary-table-wrap tfoot td {
  border-bottom: 0;
  border-top: 1px solid var(--line);
  padding-top: 10px;
  font-weight: 550;
  font-variant-numeric: tabular-nums;
}

/*
   The stability marker is positioned by both edges rather than by a centre and
   a width, because it has two shapes: a small travelling oval while the reading
   is still moving, and the whole green region once it is settled. Animating
   between them wants two real lengths — a width transitioning to `auto` snaps.

   Locked is a state, not a position, so nothing moves inside the region.
*/
#stabilityCursor {
  width: auto;
  transform: none;
  border-radius: 20px;
  transition:
    left 0.65s linear,
    right 0.65s linear,
    background 0.3s;
}
@media (prefers-reduced-motion: reduce) {
  #stabilityCursor {
    transition: none;
  }
}
